import React, { useState, useEffect } from 'react';
import portalApi, { getPortalErrorMessage } from '@/lib/portalApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Pill, Clock, Calendar, RefreshCw, Search, User, AlertCircle, Info } from 'lucide-react';

function MedicationCard({ medication }) {
  return (
    <Card className="hover:border-primary/30 transition-colors" data-testid={`medication-${medication.name}`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Icon */}
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Pill className="h-6 w-6 text-primary" />
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-base">{medication.name}</h3>
                <p className="text-sm text-muted-foreground">{medication.dosage}</p>
              </div>
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                Active
              </Badge>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {medication.form && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Form</p>
                  <p className="font-medium">{medication.form}</p>
                </div>
              )}
              {medication.route && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Route</p>
                  <p className="font-medium">{medication.route}</p>
                </div>
              )}
              {medication.frequency && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Frequency</p>
                  <p className="font-medium">{medication.frequency}</p>
                </div>
              )}
              {medication.prescribedBy && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Prescribed By</p>
                  <p className="font-medium">{medication.prescribedBy}</p>
                </div>
              )}
            </div>

            {(medication.startDate || medication.endDate) && (
              <div className="flex items-center gap-4 mt-3 pt-3 border-t text-sm text-muted-foreground">
                {medication.startDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Started: {new Date(medication.startDate).toLocaleDateString('en-AU')}
                  </div>
                )}
                {medication.endDate && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Until: {new Date(medication.endDate).toLocaleDateString('en-AU')}
                  </div>
                )}
              </div>
            )}

            {medication.instructions && (
              <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">{medication.instructions}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MedicationSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="flex-1">
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-4 w-24 mb-4" />
            <div className="grid grid-cols-4 gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PortalMedicationsPage() {
  const [loading, setLoading] = useState(true);
  const [medications, setMedications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMedications = async () => {
    setLoading(true);
    try {
      const res = await portalApi.get('/portal/participant/medications');
      setMedications(res.data.data || []);
    } catch (err) {
      toast.error(getPortalErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedications();
  }, []);

  const filteredMedications = medications.filter(med => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      med.name?.toLowerCase().includes(query) ||
      med.dosage?.toLowerCase().includes(query) ||
      med.prescribedBy?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Medications</h1>
          <p className="text-muted-foreground">Your active medications and prescriptions</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchMedications} disabled={loading} data-testid="refresh-medications">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search medications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-medications"
            />
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card className="bg-amber-500/5 border-amber-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-600">Important Notice</p>
              <p className="text-sm text-muted-foreground">
                This is a summary of your medications for reference only. Always follow your healthcare provider's 
                instructions. If you have questions about your medications, please contact your care team.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Medications List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <MedicationSkeleton key={i} />)}
        </div>
      ) : filteredMedications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Pill className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-lg font-medium">No Medications Found</p>
            <p className="text-sm text-muted-foreground">
              {searchQuery ? 'Try adjusting your search' : 'No active medications on file'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMedications.map((medication, idx) => (
            <MedicationCard key={idx} medication={medication} />
          ))}
        </div>
      )}

      {/* Summary */}
      {!loading && filteredMedications.length > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {filteredMedications.length} active medication{filteredMedications.length !== 1 ? 's' : ''}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
